import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import apiService from '../../services/api';
import {
  X,
  RefreshCw,
} from 'lucide-react';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import LoadingSpinner from '../../components/UI/LoadingSpinner';

const RevisionBOQPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [creatingRevision, setCreatingRevision] = useState(false);
  const [revisionNumber, setRevisionNumber] = useState<number>(1);
  const [originalBOQ, setOriginalBOQ] = useState<any>(null);
  const [existingRevisions, setExistingRevisions] = useState<number[]>([]);
  const [availableRevisionNumbers, setAvailableRevisionNumbers] = useState<number[]>([]);

  useEffect(() => {
    if (id) {
      fetchOriginalBOQ();
      fetchExistingRevisions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchOriginalBOQ = async () => {
    try {
      setLoading(true);
      const response = await apiService.getBOQ(id!);
      setOriginalBOQ(response.data);
    } catch (error: any) {
      console.error('Error fetching BOQ:', error);
      toast.error('Failed to load BOQ');
      navigate('/boqs');
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingRevisions = async () => {
    try {
      const boqResponse = await apiService.getBOQ(id!);
      const boq = boqResponse.data;

      let rootParentId = boq._id;
      let current = boq;

      while (current.parentBOQ) {
        const parentId = typeof current.parentBOQ === 'object'
          ? current.parentBOQ._id
          : current.parentBOQ;

        try {
          const parentResponse = await apiService.getBOQ(parentId);
          current = parentResponse.data;
          if (!current.parentBOQ) {
            rootParentId = current._id;
            break;
          }
        } catch (err) {
          break;
        }
      }

      const allBOQsResponse = await apiService.getBOQs();
      const allBOQs = Array.isArray(allBOQsResponse.data) ? allBOQsResponse.data : [];

      const revisions = allBOQs.filter((b: any) => {
        const bParentId = typeof b.parentBOQ === 'object'
          ? b.parentBOQ?._id
          : b.parentBOQ;
        return bParentId === rootParentId;
      });

      const usedNumbers = revisions
        .map((r: any) => r.version)
        .filter((v: any) => v !== undefined && v !== null && typeof v === 'number')
        .sort((a: number, b: number) => a - b);

      setExistingRevisions(usedNumbers);

      const allNumbers = Array.from({ length: 20 }, (_, i) => i + 1);
      const available = allNumbers.filter(num => !usedNumbers.includes(num));
      setAvailableRevisionNumbers(available);

      if (available.length > 0 && !available.includes(revisionNumber)) {
        setRevisionNumber(available[0]);
      } else if (available.length === 0) {
        toast.error('All revision numbers (1-20) are already used');
      }
    } catch (error: any) {
      console.error('Error fetching existing revisions:', error);
      setAvailableRevisionNumbers(Array.from({ length: 20 }, (_, i) => i + 1));
    }
  };

  const handleCreateRevision = async () => {
    if (!id) return;

    try {
      setCreatingRevision(true);

      const response = await apiService.reviseBOQ(id, revisionNumber);
      const revision = response.data.boq;

      toast.success(`Revision ${revisionNumber} created successfully!`);

      navigate(`/boqs/edit/${revision._id}`);
    } catch (error: any) {
      console.error('Error creating revision:', error);
      const errorMessage = error.response?.data?.error
        || error.response?.data?.message
        || 'Failed to create revision';
      toast.error(errorMessage);
    } finally {
      setCreatingRevision(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-12">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white border-b border-gray-200 -mx-6 px-6 -mt-6 py-4 sticky top-0 z-10 shadow-sm"
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'var(--font-family)' }}>
              Create BOQ Revision
            </h1>
            <p className="text-sm text-gray-500 mt-1" style={{ fontFamily: 'var(--font-family)' }}>
              Create a revision of BOQ: {originalBOQ?.boqNumber || 'N/A'}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/boqs')} icon={<X className="w-4 h-4" />}>
            Cancel
          </Button>
        </div>
      </motion.div>

      {/* Revision Selection */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <Card padding="md" className="max-w-2xl mx-auto">
          <div className="flex items-center mb-4">
            <RefreshCw className="w-5 h-5 text-purple-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900" style={{ fontFamily: 'var(--font-family)' }}>
              Select Revision Number
            </h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Revision Number *
              </label>
              <select
                value={revisionNumber}
                onChange={(e) => setRevisionNumber(parseInt(e.target.value))}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                style={{ fontFamily: 'var(--font-family)' }}
              >
                {availableRevisionNumbers.length > 0 ? (
                  availableRevisionNumbers.map((num) => (
                    <option key={num} value={num}>
                      Revision {num}
                    </option>
                  ))
                ) : (
                  <option value="">No available revision numbers</option>
                )}
              </select>
              {existingRevisions.length > 0 && (
                <p className="mt-1 text-xs text-gray-500">
                  Already used: {existingRevisions.join(', ')}
                </p>
              )}
              <p className="mt-2 text-sm text-gray-500">
                The BOQ number will be: <strong>
                  {originalBOQ?.boqNumber
                    ? originalBOQ.boqNumber.replace(/-rev-\d+$/, '')
                    : 'N/A'}-rev-{revisionNumber}
                </strong>
              </p>
            </div>

            {originalBOQ && (
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Original BOQ Details</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">BOQ Number:</span>
                    <span className="ml-2 font-medium">{originalBOQ.boqNumber}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Customer:</span>
                    <span className="ml-2 font-medium">
                      {typeof originalBOQ.customer === 'object'
                        ? originalBOQ.customer?.name || originalBOQ.customer?.company
                        : 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Status:</span>
                    <span className="ml-2 font-medium capitalize">{originalBOQ.status}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Project:</span>
                    <span className="ml-2 font-medium">{originalBOQ.projectName || 'N/A'}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <Button
                variant="outline"
                onClick={() => navigate('/boqs')}
                icon={<X className="w-4 h-4" />}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateRevision}
                loading={creatingRevision}
                icon={<RefreshCw className="w-4 h-4" />}
              >
                Create Revision
              </Button>
            </div>
          </div>
        </Card>
      </motion.div>
    </div>
  );
};

export default RevisionBOQPage;
